import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPDropdown } from "../CPDropdown";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { AccountsReceivableDesktop } from "./AccountsReceivableDesktop";
import { AccountsReceivableDashboard } from "./AccountsReceivableDashboard";
import {
  buildAccountsReceivableDashboard,
  enrichInvoiceForReceivables,
  filterReceivableInvoices,
  loadReceivableInvoices,
  sortReceivableInvoices,
} from "../../helpers/accountsReceivable";
import { resolveInvoiceStatus } from "../../helpers/invoices";
import { formatMoneyFromCents, toCents } from "../../helpers/invoiceMoney";
import { getInvoiceStatusPillStyle } from "../../helpers/invoiceStatusUi";
import { formatSupabaseError } from "../../helpers/estimates";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { getInvoiceCollectionStatus } from "../../helpers/invoiceDueStatus";
import {
  InvoiceDueDateSummary,
  InvoiceDueStatusBadge,
} from "../invoice/InvoiceDueStatus";
import { ClientStatementScreen } from "./ClientStatementScreen";
import { AccountantCsvExport } from "../invoice/AccountantCsvExport";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "18px 20px",
  marginBottom: "14px",
  cursor: "pointer",
};

const SEARCH_INPUT_STYLE = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid rgba(201,168,76,0.25)",
  background: "rgba(255,255,255,0.04)",
  color: "white",
  fontSize: "16px",
  boxSizing: "border-box",
};

const FILTER_BTN_BASE = {
  borderRadius: "999px",
  padding: "8px 12px",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)",
  background: "rgba(255,255,255,0.06)",
  color: "rgba(255,255,255,0.82)",
};

function ReceivableCard({ invoice, onOpen }) {
  const status = resolveInvoiceStatus(invoice.status);
  const pillStyle = getInvoiceStatusPillStyle(status);
  const collectionStatus = getInvoiceCollectionStatus(invoice);
  const overdueBorder =
    collectionStatus === "overdue"
      ? "1px solid rgba(224,107,90,0.45)"
      : "1px solid rgba(201,168,76,0.25)";

  return (
    <div
      style={{ ...CARD_STYLE, border: overdueBorder }}
      onClick={() => onOpen(invoice)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onOpen(invoice);
        }
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "4px" }}>
            {invoice.invoice_number}
          </div>
          <div style={{ opacity: 0.85, marginBottom: "4px" }}>
            {invoice.clients?.client_name || "No client"}
          </div>
          <div style={{ opacity: 0.65, fontSize: "13px" }}>
            {invoice.projects?.project_name || invoice.title || "—"}
          </div>
          <InvoiceDueDateSummary invoice={invoice} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
          <InvoiceDueStatusBadge invoice={invoice} />
          <div
            style={{
              ...pillStyle,
              borderRadius: "999px",
              padding: "5px 10px",
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {invoice.paymentStateLabel || "—"}
          </div>
          <div style={{ fontWeight: 800, color: "#4ee2a1" }}>
            {formatMoneyFromCents(
              status === "sent"
                ? invoice.outstandingCents
                : toCents(invoice.total_amount),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Accounts Receivable list + metrics.
 * Invoice opens navigate via onOpenInvoice into the shared Invoice Hub workflow
 * (resolveInvoiceHubView) — never embed Review/Summary inside this screen.
 */
export function AccountsReceivableHubScreen({
  onBack,
  currentUser,
  onOpenInvoice,
}) {
  const isDesktop = useIsDesktopViewport();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hubError, setHubError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("sent");
  const [sortKey, setSortKey] = useState("due_date");
  const [statementOpen, setStatementOpen] = useState(false);

  const reloadHub = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setInvoices([]);
      return;
    }

    const rows = await loadReceivableInvoices(currentUser);
    setInvoices(rows.map((row) => enrichInvoiceForReceivables(row)));
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setLoading(true);
      setHubError("");
      try {
        await reloadHub();
      } catch (error) {
        if (!cancelled) {
          setHubError(formatSupabaseError(error));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, [reloadHub]);

  const dashboardMetrics = useMemo(
    () => buildAccountsReceivableDashboard(invoices),
    [invoices],
  );

  const filteredInvoices = useMemo(() => {
    const filtered = filterReceivableInvoices(invoices, {
      search: searchTerm,
      statusFilter,
    });
    return sortReceivableInvoices(filtered, sortKey);
  }, [invoices, searchTerm, statusFilter, sortKey]);

  const handleHardwareBack = useCallback(() => {
    if (statementOpen) {
      setStatementOpen(false);
      return true;
    }
    if (typeof onBack !== "function") return false;
    onBack();
    return true;
  }, [onBack, statementOpen]);

  useHardwareBackHandler(handleHardwareBack);

  function openInvoice(invoice) {
    if (typeof onOpenInvoice !== "function") {
      console.error(
        "[AccountsReceivable] onOpenInvoice is required to launch Invoice Hub.",
      );
      return;
    }
    onOpenInvoice(invoice);
  }

  if (statementOpen) {
    return (
      <ClientStatementScreen
        currentUser={currentUser}
        onBack={() => setStatementOpen(false)}
      />
    );
  }

  if (isDesktop) {
    return (
      <AccountsReceivableDesktop
        onBack={onBack}
        loading={loading}
        hubError={hubError}
        metrics={dashboardMetrics}
        invoices={invoices}
        filtered={filteredInvoices}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        sortKey={sortKey}
        onSortChange={setSortKey}
        onOpen={openInvoice}
        onOpenStatement={() => setStatementOpen(true)}
      />
    );
  }

  return (
    <CrewPilotModuleShell
      title="Accounts Receivable"
      subtitle="Outstanding invoices and collections"
      sectionLabel="Finance"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
    >
      <div className="acsv-entry">
        <AccountantCsvExport kinds={["payments"]} buttonLabel="Export payments CSV" />
      </div>
      <AccountsReceivableDashboard metrics={dashboardMetrics} loading={loading} />

      <button
        type="button"
        onClick={() => setStatementOpen(true)}
        style={{
          ...FILTER_BTN_BASE,
          marginBottom: "14px",
          border: "1px solid rgba(201,168,76,0.45)",
          background: "rgba(201,168,76,0.12)",
          color: "#c9a84c",
        }}
      >
        Statement of Account
      </button>

      <input
        type="search"
        placeholder="Search invoices, clients, projects…"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        style={SEARCH_INPUT_STYLE}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", margin: "14px 0" }}>
        {[
          { value: "sent", label: "Outstanding" },
          { value: "paid", label: "Paid" },
          { value: "all", label: "All" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            style={{
              ...FILTER_BTN_BASE,
              ...(statusFilter === option.value
                ? {
                    border: "1px solid rgba(201,168,76,0.45)",
                    background: "rgba(201,168,76,0.12)",
                    color: "#c9a84c",
                  }
                : {}),
            }}
            onClick={() => setStatusFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: "14px" }}>
        <CPDropdown
          label="Sort by"
          value={sortKey}
          onChange={setSortKey}
          includePlaceholder={false}
          options={[
            { value: "due_date", label: "Due Date" },
            { value: "amount", label: "Amount" },
            { value: "client", label: "Client" },
            { value: "invoice_date", label: "Invoice Date" },
            { value: "status", label: "Status" },
          ]}
        />
      </div>

      {hubError ? (
        <div style={{ color: "#e06b5a", marginBottom: "12px" }}>{hubError}</div>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.7 }}>Loading receivables…</p>
      ) : filteredInvoices.length === 0 ? (
        <p className="kpi-empty kpi-empty--pad">No receivable invoices found.</p>
      ) : (
        filteredInvoices.map((invoice) => (
          <ReceivableCard
            key={invoice.invoice_id}
            invoice={invoice}
            onOpen={openInvoice}
          />
        ))
      )}
    </CrewPilotModuleShell>
  );
}
